
import { ConnectionListings } from '@graphql-box/react';
import { type Screen, ScreenType } from '@tmdb-graphql-api/schema/types';
import { type ReactElement, useRef } from 'react';
import { formatMoviesAndTvResults } from '#helpers/formatMoviesAndTvResults.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { type TileItem } from '../Tile/types.ts';
import { TileCarousel } from '../TileCarousel/index.tsx';

const queries = {
  [ScreenType.Movie]: "\nquery GetMovieSimilar(\n  $after: String\n  $before: String\n  $first: Int = 10\n  $id: ID!\n  $last: Int\n) {\n  movie(id: $id) {\n    similarMovies (\n      after: $after,\n      before: $before,\n      first: $first,\n      last: $last,\n    ) {\n      ...MovieListings\n    }\n  }\n}\n\nfragment MovieListings on MovieConnection {\n  edges {\n    cursor\n    node {\n      posterPath\n      status\n      title\n      voteAverage\n      voteCount\n    }\n  }\n  pageInfo {\n    hasNextPage\n    hasPreviousPage\n    startCursor\n    endCursor\n  }\n  totalCount\n}\n",
  [ScreenType.Tv]: "\nquery GetTvSimilar(\n  $after: String\n  $before: String\n  $first: Int = 10\n  $id: ID!\n  $last: Int\n) {\n  tv(id: $id) {\n    similarTvShows (\n      after: $after,\n      before: $before,\n      first: $first,\n      last: $last,\n    ) {\n      ...TvListings\n    }\n  }\n}\n\nfragment TvListings on TvConnection {\n  edges {\n    cursor\n    node {\n      name\n      posterPath\n      status\n      voteAverage\n      voteCount\n    }\n  }\n  pageInfo {\n    hasNextPage\n    hasPreviousPage\n    startCursor\n    endCursor\n  }\n  totalCount\n}\n",
};

const requestPaths = {
  [ScreenType.Movie]: 'movie.similarMovies',
  [ScreenType.Tv]: 'tv.similarTvShows',
};

export type Props = {
  children?: (element?: ReactElement) => ReactElement | undefined;
  id: string | number;
  setActivePreview?: (item: Pick<TileItem, 'id' | 'type'>) => void;
  type: ScreenType;
};

export const SimilarCarousel = ({ children, id, setActivePreview, type }: Props) => {
  const query = queries[type];
  const { config } = useToolbelt();
  const intersectionRootRef = useRef<HTMLDivElement>(null);

  return (
    <ConnectionListings<Screen>
      componentName="SimilarCarousel"
      intersectionRoot={intersectionRootRef.current}
      query={query}
      requestPath={requestPaths[type]}
      resultsPerRequest={10}
      variables={{ id }}
    >
      {({ listings, ...rest }) => {
        const tileCarousel = (
          <TileCarousel
            items={formatMoviesAndTvResults(listings, type, config)}
            scrollRef={intersectionRootRef}
            setActivePreview={setActivePreview}
            {...rest}
          />
        );

        // eslint-disable-next-line unicorn/no-null
        return (children ? children(tileCarousel) : tileCarousel) ?? null;
      }}
    </ConnectionListings>
  );
};
